import React from 'react';
import { Plus, Trash2, TrendingDown, Receipt } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

export const ExpensesTab = ({ expenses, expenseSummary, expenseFilter, setExpenseFilter, setShowExpenseDialog, deleteExpense }) => {
  return (
    <div data-testid="expenses-content">
      <div className="flex flex-wrap gap-4 mb-6 items-end justify-between">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <Label className="text-sm text-[#8E9A95]">Category</Label>
            <Select value={expenseFilter.category} onValueChange={(v) => setExpenseFilter(prev => ({ ...prev, category: v }))}>
              <SelectTrigger className="w-40 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="supplies">Supplies</SelectItem>
                <SelectItem value="salary">Salary</SelectItem>
                <SelectItem value="utilities">Utilities</SelectItem>
                <SelectItem value="food">Food & Kitchen</SelectItem>
                <SelectItem value="transport">Transport</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-[#8E9A95]">From</Label>
            <Input type="date" value={expenseFilter.start} onChange={(e) => setExpenseFilter(prev => ({ ...prev, start: e.target.value }))} className="mt-1 w-40" />
          </div>
          <div>
            <Label className="text-sm text-[#8E9A95]">To</Label>
            <Input type="date" value={expenseFilter.end} onChange={(e) => setExpenseFilter(prev => ({ ...prev, end: e.target.value }))} className="mt-1 w-40" />
          </div>
        </div>
        <Button onClick={() => setShowExpenseDialog(true)} className="bg-[#D4AF37] hover:bg-[#D4AF37]" data-testid="add-expense-btn"><Plus size={16} className="mr-2" /> Add Expense</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#121816] p-5 rounded-lg border border-[#D4AF37]/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-900/30 rounded-full flex items-center justify-center"><TrendingDown size={20} className="text-red-400" /></div>
            <div><p className="text-sm text-[#8E9A95]">Total Expenses</p><p className="text-xl font-semibold text-red-400">₹{(expenseSummary.total_expenses || 0).toLocaleString()}</p></div>
          </div>
        </div>
        <div className="bg-[#121816] p-5 rounded-lg border border-[#D4AF37]/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center"><Receipt size={20} className="text-blue-600" /></div>
            <div><p className="text-sm text-[#8E9A95]">Total Records</p><p className="text-xl font-semibold text-[#E8E3D9]">{expenseSummary.count || 0}</p></div>
          </div>
        </div>
        <div className="bg-[#121816] p-5 rounded-lg border border-[#D4AF37]/30">
          <p className="text-sm text-[#8E9A95] mb-2">By Category</p>
          <div className="space-y-1">
            {Object.entries(expenseSummary.by_category || {}).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([cat, amt]) => (
              <div key={cat} className="flex justify-between text-xs">
                <span className="capitalize text-[#8E9A95]">{cat}</span>
                <span className="font-medium text-[#E8E3D9]">₹{amt.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {expenses.length === 0 ? (
        <div className="bg-[#121816] p-12 rounded-lg border border-[#D4AF37]/30 text-center">
          <TrendingDown size={48} className="text-[#C9BFA8] mx-auto mb-4" />
          <h3 className="font-serif text-xl text-[#E8E3D9] mb-2">No Expenses Recorded</h3>
          <p className="text-[#8E9A95]">Start tracking resort expenses.</p>
        </div>
      ) : (
        <div className="bg-[#121816] rounded-lg border border-[#D4AF37]/30 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Added By</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map(expense => (
                  <TableRow key={expense.id}>
                    <TableCell>{fmtDate(expense.date)}</TableCell>
                    <TableCell><span className="capitalize bg-[#0B0F0E] px-2 py-1 rounded text-xs font-medium">{expense.category}</span></TableCell>
                    <TableCell className="font-medium">{expense.description}</TableCell>
                    <TableCell className="text-red-400 font-medium">₹{expense.amount.toLocaleString()}</TableCell>
                    <TableCell className="capitalize">{expense.payment_method}</TableCell>
                    <TableCell>{expense.created_by}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => deleteExpense(expense.id)} className="text-red-400" data-testid={`delete-expense-${expense.id}`}><Trash2 size={16} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};
